import './NavbarSearch.css';
import React, { useState, useRef } from 'react';
import classnames from 'classnames';
import { SelectableList } from '../SelectableList/SelectableList';
import { Icon } from '../Icon/Icon';
import { Event } from '../../model/event';
import { Product } from '../../model/product';
import { NavLink } from 'react-router-dom';
import {
  eventsPath,
  productsPath,
  ordersPath,
  companiesPath,
  eventPath,
  productPath,
  companyPath,
  orderPath,
} from '../../routes';
import { dig, formatDate, useErrorHandler } from '../../util/util';
import { fullUserName } from '../../model/user';
import { useRemoteSearch, globalSearch, SearchResult, SearchResultType } from '../../model/data';
import { t } from '../../locales/translate';
import { StatusPill } from '../StatusPill/StatusPill';
import { Company } from '../../model/company';
import { Order } from '../../model/order';
import { Tooltip } from '../Tooltip/Tooltip';

export function NavbarSearch(): JSX.Element {
  const [query, setQuery] = useState('');
  const [results, loading, error] = useRemoteSearch(query, globalSearch);
  const [displayResults, setDisplayResults] = useState(false);

  useErrorHandler(error);

  const inputElement = useRef<HTMLInputElement | null>(null);

  function renderLabel(key: string): string {
    switch (key) {
      case 'events':
        return t.model.event._plural;
      case 'merchProducts':
        return `${t.model.product.merch} ${t.model.product._plural}`;
      case 'companies':
        return t.model.company._plural;
      default:
        return key;
    }
  }

  function getLink(item: SearchResult[] | SearchResult, key: SearchResultType): string {
    if (Array.isArray(item)) {
      switch (key) {
        case 'events':
          return eventsPath();
        case 'merchProducts':
          return productsPath();
        case 'companies':
          return companiesPath();
        case 'orders':
          return ordersPath();
        default:
          return '/';
      }
    } else {
      switch (key) {
        case 'events':
          return eventPath((item as Event).slug || item.id);
        case 'merchProducts':
          return productPath((item as Product).slug || item.id);
        case 'companies':
          return companyPath((item as Company).slug || item.id);
        case 'orders':
          return orderPath(item.id);
        default:
          return '/';
      }
    }
  }

  function renderHeader(collection: SearchResult[], key: SearchResultType, term: string): JSX.Element {
    return (
      <NavLink
        to={`${getLink(collection, key)}?query=*${term.trim().split(' ').join('*%26*')}*`}
        className="navbar-search__results-item navbar-search__results-item--header"
      >
        {<span>{renderLabel(key)}</span>}
        {collection.length > 2 && <span>{t.general.more(collection.length.toString(), key)}</span>}
      </NavLink>
    );
  }

  function renderResults(collection: SearchResult[], key: SearchResultType): JSX.Element[] {
    function renderEventResult(item: Event): string {
      return [item.descriptiveName, item.sku].filter(Boolean).join(' - ');
    }
    function renderProductResult(item: Product): string {
      return [item.sku, item.descriptiveName].filter(Boolean).join(' - ') || t.model.product.untitledProduct;
    }
    function renderCompanyResult(item: Company): string {
      return dig(item, 'name') || t.model.company.untitledCompany;
    }
    function renderOrderResult(item: Order): JSX.Element {
      return (
        <>
          {[formatDate(item.createdAt), item.reference, fullUserName(item.user)].filter(Boolean).join(' - ') ||
            'Untitled Order'}
          <StatusPill status={(item as Order).status} size="small"></StatusPill>
        </>
      );
    }

    function renderResultItem(item: SearchResult): JSX.Element | string {
      switch (key) {
        case 'events':
          return renderEventResult(item as Event);
        case 'merchProducts':
          return renderProductResult(item as Product);
        case 'companies':
          return renderCompanyResult(item as Company);
        case 'orders':
          return renderOrderResult(item as Order);
        default:
          return t.model.item.unknownItem;
      }
    }

    return collection.slice(0, 2).map((item) => {
      return (
        <NavLink to={getLink(item, key)} className="navbar-search__results-item" key={item.id}>
          {renderResultItem(item)}
        </NavLink>
      );
    });
  }

  function formatResults(): React.ReactNode[] {
    if (results) {
      let returnResults: React.ReactNode[] = [];
      const keys = Object.keys(results) as SearchResultType[];

      keys.forEach((key) => {
        const collection = results[key];

        returnResults = [
          ...returnResults,
          ...(collection.length > 0 ? [renderHeader(collection, key, query), ...renderResults(collection, key)] : []),
        ];
      });
      return returnResults;
    }
    return [];
  }

  return (
    <Tooltip
      open={displayResults}
      mode="click"
      onChange={(open: boolean) => setDisplayResults(open)}
      className={classnames('navbar-search', {
        'navbar-search--with-results': formatResults().length > 0 && displayResults,
      })}
      positionFixed={false}
      arrow={false}
      gap="none"
      rounded={false}
      position="bottom-start"
      reference={
        <div>
          <input
            className={classnames('navbar-search__input', {
              'navbar-search__input--displaying-results': formatResults().length > 0 && displayResults,
            })}
            ref={inputElement}
            onChange={(event) => setQuery(event.currentTarget.value)}
            onFocus={(event) => {
              setDisplayResults(true);
              setQuery(event.currentTarget.value);
            }}
          />
          <Icon loading={loading} className="navbar-search__icon" name="search--sm" />
        </div>
      }
    >
      <SelectableList
        className="navbar-search__results"
        options={formatResults()}
        onSelect={() => {
          setDisplayResults(false);
        }}
      />
    </Tooltip>
  );
}
